/**
 * Cubic-bézier easing solver.
 *
 * This is the same algorithm browsers use to evaluate a CSS
 * `cubic-bezier(x1, y1, x2, y2)` — Newton-Raphson with a bisection
 * fallback — so a GSAP tween and a CSS transition given the same four
 * numbers move identically.
 *
 * That parity is the entire reason this exists rather than GSAP's
 * CustomEase plugin. The easing tokens in styles/abstracts/_easing.scss and
 * the GSAP eases in ./eases.ts are generated from one shared set of control
 * points, so a CSS transition and a GSAP tween cannot drift apart. It also keeps a plugin out of the bundle.
 *
 * Curve: P0 = (0,0), P1 = (x1,y1), P2 = (x2,y2), P3 = (1,1).
 * Only x1 and x2 are constrained to [0,1] — y may overshoot, which is what
 * makes weighted, slightly-overshooting eases possible.
 */

const NEWTON_ITERATIONS = 8;
const NEWTON_MIN_SLOPE = 0.001;
const SUBDIVISION_PRECISION = 1e-7;
const SUBDIVISION_MAX_ITERATIONS = 12;

/* Polynomial coefficients for the bézier, expanded so evaluation is three
   multiply-adds rather than repeated interpolation. */
const coefficientA = (a1: number, a2: number): number => 1 - 3 * a2 + 3 * a1;
const coefficientB = (a1: number, a2: number): number => 3 * a2 - 6 * a1;
const coefficientC = (a1: number): number => 3 * a1;

/** Evaluates one axis of the curve at parameter t. */
const sampleCurve = (t: number, a1: number, a2: number): number =>
  ((coefficientA(a1, a2) * t + coefficientB(a1, a2)) * t + coefficientC(a1)) * t;

/** Derivative of sampleCurve, used as the Newton-Raphson slope. */
const sampleSlope = (t: number, a1: number, a2: number): number =>
  3 * coefficientA(a1, a2) * t * t + 2 * coefficientB(a1, a2) * t + coefficientC(a1);

/**
 * Bisection fallback. Slower but unconditionally convergent, so it covers
 * the flat regions where Newton-Raphson would shoot off.
 */
const binarySubdivide = (
  x: number,
  lower: number,
  upper: number,
  x1: number,
  x2: number,
): number => {
  let low = lower;
  let high = upper;
  /* No initialiser: the do-while body assigns both before either is read,
     and TypeScript's control-flow analysis knows the body always runs. */
  let t: number;
  let delta: number;
  let iterations = 0;

  do {
    t = low + (high - low) / 2;
    delta = sampleCurve(t, x1, x2) - x;
    if (delta > 0) {
      high = t;
    } else {
      low = t;
    }
    iterations += 1;
  } while (Math.abs(delta) > SUBDIVISION_PRECISION && iterations < SUBDIVISION_MAX_ITERATIONS);

  return t;
};

/** Finds the parameter t at which the curve's x equals the given progress. */
const solveForT = (x: number, x1: number, x2: number): number => {
  let t = x;

  for (let i = 0; i < NEWTON_ITERATIONS; i += 1) {
    const slope = sampleSlope(t, x1, x2);
    /* A near-zero slope means Newton-Raphson would divide by ~0 and
       diverge; hand off to bisection instead. */
    if (slope < NEWTON_MIN_SLOPE) {
      return binarySubdivide(x, 0, 1, x1, x2);
    }
    t -= (sampleCurve(t, x1, x2) - x) / slope;
  }

  return t;
};

/**
 * Builds an easing function from four control-point coordinates, matching
 * CSS `cubic-bezier(x1, y1, x2, y2)`.
 *
 * @throws RangeError if x1 or x2 falls outside [0, 1], which would make the
 *         curve non-monotonic in x and the progress lookup ambiguous.
 */
export const cubicBezier = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): ((progress: number) => number) => {
  if (x1 < 0 || x1 > 1 || x2 < 0 || x2 > 1) {
    throw new RangeError(
      `cubic-bezier x coordinates must be within [0, 1]; received x1=${String(x1)}, x2=${String(x2)}.`,
    );
  }

  /* A straight line needs no solving. Present for completeness only —
     see ./eases.ts, where linear is deliberately not offered. */
  if (x1 === y1 && x2 === y2) {
    return (progress: number) => progress;
  }

  return (progress: number): number => {
    /* GSAP clamps progress to [0,1] for normal playback, but a timeline
       being scrubbed backwards past its start can pass values outside it. */
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;
    return sampleCurve(solveForT(progress, x1, x2), y1, y2);
  };
};
